import { RoundIcon } from "@/shared/ui";

/**
 * Схема «сеть отправления → защита → сеть зачисления».
 *
 * Декорация, объясняющая смысл следующего шага: сеть должна совпасть, иначе
 * деньги потеряются. Ниже об этом же написано словами, а картинка держит
 * внимание на выборе.
 *
 * Макет нарисован ровно на две сети, а `deposit_assets` задаёт оператор и там
 * может оказаться одна или пять. Поэтому лишние схлопываются в «+N», а
 * единственная встаёт по центру: схема без второй стороны бессмысленна.
 *
 * Сцена абсолютная внутри своего контейнера с фиксированной пропорцией — так
 * координаты макета переносятся один раз, а наружу блок остаётся обычным
 * элементом потока.
 */
interface DepositFlowProps {
  networks: string[];
  /** Подпись для «+N», уже собранная вызывающим: текст живёт в ru.json. */
  moreLabel: (count: number) => string;
}

export function DepositFlow({ networks, moreLabel }: DepositFlowProps) {
  const [left, right] = networks;
  const extra = networks.length - 2;

  return (
    <div
      aria-hidden="true"
      className="relative mx-auto aspect-[286/83] w-full max-w-[286px] text-status-profit"
    >
      {left ? <NetworkPill label={left} className="left-0" /> : null}
      {right ? (
        <NetworkPill
          label={extra > 0 ? moreLabel(extra) : right}
          className="right-0"
        />
      ) : null}

      {/* Соединители. `preserveAspectRatio="none"` — кривые тянутся вместе с
          контейнером, как и всё остальное в сцене. */}
      <svg
        viewBox="0 0 210 64"
        preserveAspectRatio="none"
        className="absolute inset-x-[14%] top-[11%] h-[77%] overflow-visible"
      >
        {left ? (
          <path
            d="M0 8 H38 C55 8 58 31 80 31 H96"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.58"
            strokeWidth="1"
          />
        ) : null}
        {right ? (
          <path
            d="M210 8 H172 C155 8 152 31 130 31 H114"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.58"
            strokeWidth="1"
          />
        ) : null}
        <path
          d="M0 39 H96M114 39H210"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.28"
          strokeWidth="1"
          strokeDasharray="2 2"
        />
        <circle cx="26" cy="8" r="1.6" fill="currentColor" />
        <circle cx="184" cy="8" r="1.6" fill="currentColor" />
      </svg>

      {/* Двойная аура собрана расширением тени без размытия — так она читается
          как два кольца, а не как одно мягкое пятно. */}
      <span
        className={[
          "absolute top-[29%] left-1/2 flex size-[41px] -translate-x-1/2 items-center justify-center",
          "rounded-full border border-border-subtle",
          "bg-[radial-gradient(circle,color-mix(in_srgb,var(--color-status-profit)_18%,var(--palette-panel)),var(--palette-bg)_66%)]",
          "shadow-[0_0_0_9px_color-mix(in_srgb,var(--color-status-profit)_3%,transparent),0_0_0_18px_color-mix(in_srgb,var(--color-status-profit)_2%,transparent)]",
        ].join(" ")}
      >
        <RoundIcon icon="shieldCheck" size={39} tone="green" glow={false} className="border-0 bg-none" />
      </span>

      <FlowCheck className="left-[20%]" />
      <FlowCheck className="right-[20%]" />
    </div>
  );
}

function NetworkPill({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={[
        "absolute top-[5%] flex h-[22px] min-w-[48px] items-center justify-center rounded-full px-2.5",
        "border border-status-profit/20 bg-status-profit/10 text-caption text-status-profit",
        className,
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function FlowCheck({ className }: { className: string }) {
  return (
    <span
      className={[
        "absolute top-[52%] flex size-[17px] items-center justify-center rounded-full",
        "border-[3px] border-background-base bg-status-profit text-text-inverse",
        "shadow-[0_0_6px_color-mix(in_srgb,var(--color-status-profit)_20%,transparent)]",
        className,
      ].join(" ")}
    >
      <svg viewBox="0 0 24 24" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="4">
        <path d="M5 12l5 5 9-9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
